import { MapPin, Car, Users, Calendar, Clock, Luggage, Shield } from 'lucide-react';

// Composants de base réutilisables
export const InfoItem = ({ label, value, icon, config }) => {
  const mergedConfig = { ...config };
  
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      fontFamily: mergedConfig.fontFamily
    }}>
      <div style={{
        fontSize: mergedConfig.fontSize * 0.9,
        color: mergedConfig.primaryColor,
        fontWeight: 500
      }}>
        {label}
      </div>
      <div style={{
        fontSize: mergedConfig.fontSize,
        color: mergedConfig.textColor || '#1f2937',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        gap: 6
      }}>
        {icon} {value}
      </div>
    </div>
  );
};

export const TransferSection = ({ data, config, t, formatDate, formatDateTime, getServiceIcon }) => {
  const formatTime = (timeString) => {
    if (!timeString) return t('notAvailable');
    try {
      const [hours, minutes] = timeString.split(':');
      return `${hours}:${minutes}`;
    } catch {
      return t('notAvailable');
    }
  };

  const formatFullDateTime = (dateString, timeString, lang = 'en') => {
    if (!dateString || !timeString) return t('notAvailable');
    try {
      const date = new Date(`${dateString}T${timeString}`);
      return date.toLocaleString(lang === 'fr' ? 'fr-FR' : lang === 'es' ? 'es-ES' : 'en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return t('notAvailable');
    }
  };

  // Utiliser les données de la section
  const transferData = data.booking_transfer || data;

  return (
    <div className='pdf-section' style={{
      background: 'transparent',
      marginBottom: config.gap
    }}>
      <div style={{ 
        marginBottom: 0,
        fontFamily: config.fontFamily,
        background: config.sectionColor,
        padding: '20px',
        borderRadius: '8px',
        border: `1px solid ${config.sectionColor}40`
      }}>
        <div style={{
          fontSize: `calc(${config.fontSize || 18}px * 1.5)`,
          fontWeight: 'bold',
          color: config.primaryColor,
          marginBottom: 20,
          paddingBottom: 8,
          borderBottom: `2px solid ${config.primaryColor}`,
          textTransform: 'uppercase',
          letterSpacing: '1px',
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          {getServiceIcon ? getServiceIcon('transfer') : <Car size={20} />}
          {data.name || t('transferInformation')}
        </div>

        {/* Informations générales du transfert */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: config.columns > 1 ? `repeat(${config.columns}, 1fr)` : 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 20
        }}>
          <InfoItem
            config={config}
            label={t('transferType')}
            value={transferData.type === 'Oneway' ? t('oneWayTransfer') :
                   transferData.type === 'Roundtrip' ? t('roundTripTransfer') :
                   transferData.type || t('notAvailable')}
            icon={<Car size={16} />}
          />

          {transferData.itineraries?.[0]?.travelType && (
            <InfoItem
              config={config}
              label={t('travelType')}
              value={transferData.itineraries[0].travelType === 'flight' ? t('flightTransfer') :
                     transferData.itineraries[0].travelType === 'train' ? t('trainTransfer') :
                     transferData.itineraries[0].travelType || t('notAvailable')}
              icon={<Shield size={16} />}
            />
          )}

          {transferData.itineraries?.[0]?.travelNumber && (
            <InfoItem
              config={config}
              label={t('travelNumber')}
              value={transferData.itineraries[0].travelNumber}
            />
          )}

          {/* Passagers */}
          {(transferData.adult > 0 || transferData.child > 0 || transferData.infant > 0 || 
            transferData.senior > 0 || transferData.baby > 0) && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4
            }}>
              <div style={{
                fontSize: config.fontSize * 0.9,
                color: config.primaryColor,
                fontWeight: 500
              }}>
                {t('passengers')}
              </div>
              <div style={{
                fontSize: config.fontSize,
                color: config.textColor || '#1f2937',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap'
              }}>
                {transferData.adult > 0 && (
                  <span>{t('adults')}: {transferData.adult}</span>
                )}
                {transferData.child > 0 && (
                  <span>• {t('children')}: {transferData.child}</span>
                )}
                {transferData.infant > 0 && (
                  <span>• {t('infants')}: {transferData.infant}</span>
                )}
                {transferData.senior > 0 && (
                  <span>• {t('seniors')}: {transferData.senior}</span>
                )}
                {transferData.baby > 0 && (
                  <span>• {t('baby')}: {transferData.baby}</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Informations des itinéraires */}
        {transferData.itineraries && transferData.itineraries.length > 0 && (
          <div style={{ marginTop: '24px' }}>
            {transferData.itineraries.map((itinerary, index) => (
              <div key={index} style={{
                marginBottom: index < transferData.itineraries.length - 1 ? '32px' : '0',
                padding: '20px',
                background: config.backgroundColor,
                borderRadius: '10px',
                border: `1px solid ${config.sectionColor}40`
              }}>
                {/* Header de l'itinéraire */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px',
                  paddingBottom: '10px',
                  borderBottom: `2px solid ${config.primaryColor}`
                }}>
                  <div style={{ 
                    fontSize: '18px', 
                    fontWeight: '600', 
                    color: config.textColor || '#1f2937' 
                  }}>
                    {t('itinerary')} {index + 1}
                  </div>
                  <div style={{ 
                    fontSize: '14px', 
                    color: config.secondaryColor 
                  }}>
                    {formatFullDateTime(itinerary.transferDate, itinerary.transferTime, data.lang || 'en')}
                  </div>
                </div>

                {/* Informations détaillées */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr', 
                  gap: '30px', 
                  alignItems: 'center' 
                }}>
                  {/* Origine */}
                  <div>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: config.primaryColor,
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <MapPin size={14} />
                      {t('origin')}
                    </div>
                    <div style={{ 
                      fontSize: '16px', 
                      fontWeight: '600', 
                      color: config.textColor || '#1f2937', 
                      marginBottom: '4px' 
                    }}>
                      {itinerary.origin?.name || t('notAvailable')}
                    </div>
                    <div style={{ 
                      fontSize: '14px', 
                      color: config.secondaryColor 
                    }}>
                      {t(itinerary.origin?.type?.toLowerCase() || 'location')}
                    </div>
                  </div>

                  {/* Destination(s) */}
                  <div>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: config.primaryColor,
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <MapPin size={14} />
                      {t('destination')}
                    </div>
                    {itinerary.destination && itinerary.destination.length > 0 ? (
                      itinerary.destination.map((dest, destIndex) => (
                        <div key={destIndex} style={{ 
                          marginBottom: destIndex < itinerary.destination.length - 1 ? '12px' : '0' 
                        }}>
                          <div style={{ 
                            fontSize: '16px', 
                            fontWeight: '600', 
                            color: config.textColor || '#1f2937', 
                            marginBottom: '4px' 
                          }}>
                            {dest.name}
                          </div>
                          <div style={{ 
                            fontSize: '14px', 
                            color: config.secondaryColor 
                          }}>
                            {t(dest.type?.toLowerCase() || 'location')}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={{ 
                        fontSize: '16px', 
                        fontWeight: '600', 
                        color: config.textColor || '#1f2937' 
                      }}>
                        {t('notAvailable')}
                      </div>
                    )}
                  </div>
                </div>

                {/* Véhicule */}
                {itinerary.vehicle && (
                  <div style={{
                    marginTop: '24px',
                    padding: '16px',
                    background: config.sectionColor,
                    borderRadius: '8px',
                    border: `1px solid ${config.sectionColor}60`
                  }}>
                    <div style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: config.primaryColor,
                      marginBottom: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <Car size={18} />
                      {t('vehicleInformation')}
                    </div>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: config.columns > 1 ? `repeat(${config.columns}, 1fr)` : 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: 20
                    }}>
                      <InfoItem
                        config={config}
                        label={t('vehicleType')}
                        value={itinerary.vehicle.type}
                      />
                      <InfoItem
                        config={config}
                        label={t('brand')}
                        value={itinerary.vehicle.brand}
                      />
                      <InfoItem
                        config={config}
                        label={t('model')}
                        value={itinerary.vehicle.model}
                      />
                      <InfoItem
                        config={config}
                        label={t('luggageCapacity')}
                        value={`${itinerary.vehicle.luggageCapacity} ${t('bags')}`}
                        icon={<Luggage size={14} />}
                      />
                      <InfoItem
                        config={config}
                        label={t('passengerCapacity')}
                        value={`${itinerary.vehicle.capacity} ${t('persons')}`}
                        icon={<Users size={14} />}
                      />
                    </div>
                  </div>
                )}

                {/* Date et heure */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  alignItems: 'center',
                  marginTop: '20px',
                  paddingTop: '16px',
                  borderTop: `1px solid ${config.sectionColor}40`
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Calendar size={16} color={config.primaryColor} />
                    <div>
                      <div style={{ 
                        fontSize: '14px', 
                        color: config.secondaryColor 
                      }}>
                        {t('date')}
                      </div>
                      <div style={{ 
                        fontSize: '15px', 
                        fontWeight: '600', 
                        color: config.textColor || '#1f2937' 
                      }}>
                        {itinerary.transferDate ? formatDate(itinerary.transferDate) : t('notAvailable')}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Clock size={16} color={config.primaryColor} />
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ 
                        fontSize: '14px', 
                        color: config.secondaryColor 
                      }}>
                        {t('time')}
                      </div>
                      <div style={{ 
                        fontSize: '15px', 
                        fontWeight: '600', 
                        color: config.textColor || '#1f2937' 
                      }}>
                        {itinerary.transferTime ? formatTime(itinerary.transferTime) : t('notAvailable')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Mettre à jour le mapping SectionComponents pour inclure ce composant
export const TransferSectionComponents = {
  TransferSection,
};

// Fonction utilitaire pour obtenir un composant par nom
export const getTransferSectionComponent = (componentName) => {
  return TransferSectionComponents[componentName] || null;
};